import ratesJson from './eur-exchange-rates.json';

/**
 * Single source of truth for currency-to-EUR conversion across the app.
 *
 * Until 2026-09-24 the same table was hand-copied into four places (lambda/indexer.ts,
 * lambda/utils/salaryAggregation.ts, the job page's salaryConversion.ts, and the salary-period
 * plausibility check) with no date on any of them, so they drifted from the market and from each
 * other. The data now lives in eur-exchange-rates.json, refreshed from the ECB's daily reference
 * rates with `npm run refresh:currency-rates` (see scripts/refresh-currency-rates.ts for why it
 * is a checked-in file and not a runtime fetch).
 *
 * Convention, kept from the original tables: EUR_PER_UNIT[code] is how many EUR one unit of that
 * currency is worth, so `amount * EUR_PER_UNIT[code]` converts to EUR.
 */
interface RatesFile {
  source: string;
  asOf: string;
  unitsPerEur: Record<string, number>;
  manual: { asOf: string; note: string; unitsPerEur: Record<string, number> };
}

const FILE = ratesJson as RatesFile;

function invert(table: Record<string, number>, out: Record<string, number>): void {
  for (const [code, unitsPerEur] of Object.entries(table)) {
    if (Number.isFinite(unitsPerEur) && unitsPerEur > 0) out[code] = 1 / unitsPerEur;
  }
}

const EUR_PER_UNIT_TABLE: Record<string, number> = { EUR: 1 };
invert(FILE.manual.unitsPerEur, EUR_PER_UNIT_TABLE);
// ECB rates win over manual ones if a currency ever appears in both.
invert(FILE.unitsPerEur, EUR_PER_UNIT_TABLE);

/** currency code -> EUR per one unit of that currency. Frozen; read-only. */
export const EUR_PER_UNIT: Readonly<Record<string, number>> = Object.freeze(EUR_PER_UNIT_TABLE);

/** Date of the ECB rates in the file (YYYY-MM-DD). */
export const EXCHANGE_RATES_AS_OF: string = FILE.asOf;

/**
 * EUR per one unit of `currency`, or undefined when the currency is unknown. Case-insensitive,
 * tolerates surrounding whitespace, and treats a missing/empty code as EUR (the pipeline's
 * historical default for jobs with a salary but no stored currency).
 */
export function getEurPerUnit(currency: string | undefined | null): number | undefined {
  const code = (currency ?? '').trim().toUpperCase();
  if (!code) return 1;
  return EUR_PER_UNIT[code];
}

/** Converts `amount` in `currency` to EUR, or undefined when the currency is unknown. */
export function convertToEur(amount: number, currency: string | undefined | null): number | undefined {
  const rate = getEurPerUnit(currency);
  return rate === undefined ? undefined : amount * rate;
}
